import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Building2,
  FilePen,
  FileText,
  Handshake,
  History,
  Landmark,
  MapPin,
  Package,
  Truck,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { dateTimeInZone } from '@/lib/dates';
import { activityLabel, rowLinks, type ActivityGroupOrOther, type ActivityItem } from '@/lib/activity';
import { cn } from '@/lib/utils';

export const GROUP_ICON: Record<ActivityGroupOrOther, LucideIcon> = {
  load_lifecycle: Package,
  load_changes: FilePen,
  load_tracking: MapPin,
  documents: FileText,
  receivables: Landmark,
  fleet: Truck,
  brokers: Handshake,
  team: Users,
  agency: Building2,
  money: Wallet,
  other: History,
};

type T = ReturnType<typeof useTranslation>['t'];

/** Who did it: the profile or driver name, else what kind of actor it was (system, Coreway support). */
export function actorText(item: ActivityItem, t: T): string {
  const name =
    item.actorName ??
    (item.actorKind === 'operator' ? t('activity.actor.operator') : item.actorKind === 'system' || !item.actorUserId ? t('activity.actor.system') : t('activity.actor.former'));
  return item.onBehalfName && item.onBehalfUserId !== item.actorUserId ? `${name} (${t('activity.actor.onBehalf', { name: item.onBehalfName })})` : name;
}

/** Where a compact row opens: its load, else the driver's sheet, else the dispatcher's card, else the page. */
export function primaryHref(item: ActivityItem, roleOf: (id: string) => string | null): string {
  const links = rowLinks(item, roleOf);
  return links.load ?? links.driver ?? links.dispatcher ?? '/activity';
}

/** The bell's compact Activity rows (design 9.3): icon by group, the line, actor, load number and time. */
export function ActivityList({
  items,
  wasUnread,
  timeZone,
  roleOf,
  onNavigate,
}: {
  items: ActivityItem[];
  /** Rows that were unread when the tab opened: they keep their dot while the panel stays open. */
  wasUnread: Set<string>;
  timeZone: string;
  roleOf: (id: string) => string | null;
  onNavigate(): void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <ul className="divide-y divide-hairline">
      {items.map((item) => {
        const Icon = GROUP_ICON[item.group];
        const unread = item.unread || wasUnread.has(item.id);
        return (
          <li key={item.id}>
            <Link
              to={primaryHref(item, roleOf)}
              onClick={onNavigate}
              className={cn('flex gap-3 px-4 py-3 hover:bg-secondary', unread && 'bg-accent-wash')}
            >
              <Icon className="mt-0.5 size-5 shrink-0 text-ink-muted" aria-hidden="true" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className={cn('text-sm text-ink', unread && 'font-semibold')}>
                  {activityLabel(item, t)}
                  {item.loadNumber && <span className="font-mono text-ink-secondary"> · {item.loadNumber}</span>}
                </span>
                <span className="truncate text-xs text-ink-muted">
                  {actorText(item, t)} · {dateTimeInZone(item.at, timeZone, i18n.resolvedLanguage ?? 'en')}
                </span>
              </span>
              {unread && (
                <span
                  data-testid="activity-unread"
                  role="img"
                  className="mt-1.5 size-2 shrink-0 rounded-full bg-accent-base"
                  aria-label={t('notifications.unread')}
                />
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
